import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { listPendingEntries, removePendingEntry, listPendingObservations, removePendingObservation, listPendingPhotos, removePendingPhoto, listPendingIrrigationLogs, removePendingIrrigationLog } from "../lib/offline";
import { CloudArrowUp, CloudSlash, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

export default function SyncStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const refresh = async () => {
    const items = await listPendingEntries();
    const obs = await listPendingObservations();
    const photos = await listPendingPhotos();
    const irr = await listPendingIrrigationLogs();
    setPending(items.length + obs.length + photos.length + irr.length);
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    try {
      const items = await listPendingEntries();
      if (items.length > 0) {
        const { data } = await api.post("/time-entries/sync", {
          entries: items.map((i) => i.payload),
        });
        for (const r of data.results) {
          if (r.ok) await removePendingEntry(r.client_id);
        }
      }
      const obsItems = await listPendingObservations();
      for (const o of obsItems) {
        try {
          await api.put(`/scouting/visits/${o.visit_id}/blocks/${o.ranch_id}`, o.payload);
          await removePendingObservation(o.id);
        } catch (e) {
          if (e.response && e.response.status < 500) await removePendingObservation(o.id);
        }
      }
      const photoItems = await listPendingPhotos();
      for (const p of photoItems) {
        try {
          const fd = new FormData();
          fd.append("file", p.blob, p.filename || "photo.jpg");
          fd.append("visit_id", p.visit_id);
          if (p.ranch_id) fd.append("ranch_id", p.ranch_id);
          fd.append("category", p.category || "General");
          if (p.caption) fd.append("caption", p.caption);
          fd.append("client_eligible", p.client_eligible ? "true" : "false");
          if (p.latitude != null) fd.append("latitude", p.latitude);
          if (p.longitude != null) fd.append("longitude", p.longitude);
          await api.post("/scouting/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await removePendingPhoto(p.id);
        } catch (e) {
          if (e.response && e.response.status < 500) await removePendingPhoto(p.id);
        }
      }
      const irrItems = await listPendingIrrigationLogs();
      for (const l of irrItems) {
        try {
          await api.post("/irrigation/logs", { ...l.payload, client_id: l.id });
          await removePendingIrrigationLog(l.id);
        } catch (e) {
          if (e.response && e.response.status < 500) await removePendingIrrigationLog(l.id);
        }
      }
      await refresh();
    } catch (e) {
      // stay queued
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (online && pending > 0 && !syncing) sync();
    // eslint-disable-next-line
  }, [online, pending]);

  const cls = "font-mono text-xs uppercase px-3 py-1 flex items-center gap-2 border border-stone-200";
  if (!online)
    return (
      <span data-testid="offline-sync-indicator" className={`${cls} bg-[#D8C7A1] text-[#252525]`}>
        <CloudSlash weight="bold" size={14} /> Offline · {pending} queued
      </span>
    );
  if (syncing)
    return (
      <span data-testid="offline-sync-indicator" className={`${cls} bg-[#1F3D34] text-white`}>
        <ArrowsClockwise weight="bold" size={14} className="animate-spin" /> Syncing…
      </span>
    );
  if (pending > 0)
    return (
      <button
        data-testid="sync-now-btn"
        onClick={sync}
        className={`${cls} bg-[#1F3D34] text-white hover:bg-zinc-800`}
      >
        <CloudArrowUp weight="bold" size={14} /> Sync {pending}
      </button>
    );
  return (
    <span data-testid="offline-sync-indicator" className={`${cls} bg-[#7C9673] text-white`}>
      <CheckCircle weight="bold" size={14} /> Live
    </span>
  );
}
